
import { format } from "date-fns";
import { Chart } from "highcharts-vue";
import { v4 as uuid } from "uuid";
import { defineComponent } from "vue";
import ModalTW from "@/components/UI/ModalTW.vue";
import PlannerRepository from "@/repositories/PlannerRepository";
import logger from "@/services/logger";
import { MilestonePlot } from "@/types/MilestonePlot";

type EventType = "plan" | "actual";
interface SeriesDataItem {
  x: number | null;
  y: number;
  name: string;
  events: {
    plan: MilestonePlot["planner_items"][0];
    actual: MilestonePlot["actual_events"][0];
  };
}

export default defineComponent({
  name: "MilestonePlotModal",
  components: {
    Chart,
    ModalTW,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close"],
  data() {
    return {
      plot: null as MilestonePlot | null,
      loading: false,
      graphModes: [
        { type: "sequence", current: true, name: this.$t("milestone_plot.sequence_mode") },
        { type: "end-date", current: false, name: this.$t("milestone_plot.end_date_mode") },
      ],
    };
  },
  mounted() {
    this.loadPlanConfig();
  },
  methods: {
    loadPlanConfig() {
      this.loading = true;
      const { customer_name, site_id } = this.$route.params;
      PlannerRepository.loadMilestonePlot(customer_name as string, site_id as string)
        .then((data) => {
          this.plot = data;
        })
        .catch((err) => {
          logger.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSeriesColor(type: EventType) {
      return type === "plan" ? "#BCCFCC" : "#336260";
    },
    switchModes(mode: string) {
      this.graphModes.forEach((item) => {
        item.current = item.type === mode;
      });
    },
    getTooltipContent(events: SeriesDataItem["events"]) {
      const name = events.plan.tags
        .slice()
        .sort((a, b) => a.type.localeCompare(b.type))
        .map((tag) => tag.name)
        .join(" | ");

      const tooltip = `
        <div style="padding:6px">
          <h4 style="font-size: 1rem; margin-top:10px; padding-bottom:4px; text-align: left;border-bottom: 1px solid lightgray">${name}</h4>
          <div style="display: flex; padding-top: 1rem;align-items:center;width:100%;justify-content: space-between">
            <span
              style="padding: 3px 4px;;border-radius: 5px;background: ${this.getSeriesColor(
                "plan",
              )}"
            >
              ${this.$t("analytics.planner.target_period")}
            </span>
            <span style="margin-left: 0.5rem;">
              ${format(new Date(events.plan.planned_event.start), "dd.MM.yyyy")} - 
              ${format(new Date(events.plan.planned_event.end), "dd.MM.yyyy")}
            </span>
          </div>
          <div style="display: flex; padding-top: 0.5rem;align-items:center;width:100%;justify-content: space-between">
            <span
              style="padding: 3px 4px;background: ${this.getSeriesColor(
                "actual",
              )};border-radius: 5px;color: white;"
            >
              ${this.$t("analytics.planner.actual_period")}
            </span>
            <span style="margin-left: 0.5rem;">
              ${
                events.actual
                  ? `${format(new Date(events.actual.start), "dd.MM.yyyy")} - 
                  ${
                    events.actual.end
                      ? format(new Date(events.actual.end), "dd.MM.yyyy")
                      : this.$t("analytics.planner.present")
                  }`
                  : this.$t("analytics.planner.actual_not_started")
              }
            </span>
          </div>
        </div>`;

      return tooltip;
    },
    splitSequences() {
      const items = this.plot?.planner_items;
      const sequences: Record<string, MilestonePlot["planner_items"]> = {};

      items?.forEach((item) => {
        item.tags.forEach((tag) => {
          if (tag.type === "section") {
            if (!sequences[tag.name]) {
              sequences[tag.name] = [item];
            } else {
              sequences[tag.name].push(item);
            }
          }
        });
      });

      for (const sequence in sequences) {
        const sequenceItems = sequences[sequence];

        if (sequenceItems.length < 2) {
          delete sequences[sequence];
          continue;
        }

        sequences[sequence].sort(
          (a, b) =>
            new Date(a.planned_event.end).getTime() - new Date(b.planned_event.end).getTime(),
        );
      }

      return sequences;
    },
    getEndDateChartData() {
      const yAxisCategories = this.plot?.planner_items?.map((item) => {
        item.tags.sort((a, b) => a.type.localeCompare(b.type));
        const tags = item.tags.map((tag) => tag.name).join(" | ");

        return tags;
      });

      const plansLevels: Record<string, number> = {};
      const actualSeriesData: SeriesDataItem[] = [];
      const zones: Record<string, number | string>[] = [];
      const plansSeriesData: SeriesDataItem[] = [];
      const getTooltipContent = this.getTooltipContent;

      const getActualBySourceId =
        this.plot?.actual_events.reduce((acc, item) => {
          acc[item.source_id] = item;

          return acc;
        }, {} as Record<string, MilestonePlot["actual_events"][0]>) || {};

      const anyActualCompleted = this.plot?.actual_events.some((item) => item.end !== null);

      this.plot?.planner_items?.forEach((item, i) => {
        plansLevels[item.source_id] = i;
        const actual = getActualBySourceId[item.source_id];

        const generalData = {
          name: item.name,
          y: i,
          events: {
            actual: actual,
            plan: item,
          },
        };

        plansSeriesData.push({
          ...generalData,
          x: new Date(item.planned_event.end).getTime(),
        });

        if (anyActualCompleted) {
          actualSeriesData.push({
            ...generalData,
            x: actual?.end ? new Date(actual.end).getTime() : null,
          });
        }
      });

      for (let i = actualSeriesData.length - 2; i >= 0; i--) {
        if (actualSeriesData[i].x === null) {
          actualSeriesData.pop();
        } else {
          break;
        }
      }

      actualSeriesData.forEach((_, i) => {
        if (actualSeriesData[i]?.x === null) {
          actualSeriesData[i].x = Date.now();

          if (zones.at(-1)?.value === i) {
            const zone = zones.at(-1) as Record<string, number | string>;
            zone.value = i + 1;
          } else {
            zones.push(
              { value: i - 1 },
              { value: i + 1, color: this.getSeriesColor("actual"), dashStyle: "Dash" },
            );
          }
        }
      });

      return {
        yAxis: {
          tickPixelInterval: 2,
          categories: yAxisCategories,
          plotLines: [],
          title: {
            text: undefined,
          },
        },
        tooltip: {
          useHTML: true,
          formatter() {
            return getTooltipContent((this as unknown as { point: SeriesDataItem }).point.events);
          },
        },
        series: [
          {
            name: "Planned",
            data: plansSeriesData,
            type: "spline",
            lineWidth: 3,
            id: uuid(),
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 4,
            },
            color: this.getSeriesColor("plan"),
          },
          ...(anyActualCompleted
            ? [
                {
                  name: "Actual",
                  data: actualSeriesData,
                  type: "spline",
                  marker: {
                    enabled: true,
                    symbol: "circle",
                    radius: 4,
                  },
                  zones,
                  lineWidth: 3,
                  id: uuid(),
                  color: this.getSeriesColor("actual"),
                },
              ]
            : []),
        ],
      };
    },
    getSequenceGroupedChartData() {
      const plannerItems = this.splitSequences();
      const yAxisCategories: string[] = [];
      const yPlotLines: unknown[] = [];
      const plansSeriesData: Record<
        string,
        { plans: SeriesDataItem[]; actual: SeriesDataItem[]; zones: Record<string, unknown>[] }
      > = {};
      const getTooltipContent = this.getTooltipContent;

      const getActualBySourceId =
        this.plot?.actual_events.reduce((acc, item) => {
          acc[item.source_id] = item;

          return acc;
        }, {} as Record<string, MilestonePlot["actual_events"][0]>) || {};

      let yLevel = 0;
      Object.entries(plannerItems).forEach(([sectionName, group], i) => {
        const categories = group.map((item) => {
          const tags = item.tags.slice().sort((a, b) => a.type.localeCompare(b.type));
          const stringTags = tags.map((tag) => tag.name).join(" | ");

          return stringTags;
        });

        yAxisCategories.push(...categories);

        const plannerItemsData: SeriesDataItem[] = [];
        const actualItemsData: SeriesDataItem[] = [];

        group.forEach((item) => {
          const actual = getActualBySourceId[item.source_id];
          const generalData = {
            name: sectionName,
            y: yLevel,
            events: {
              actual: actual,
              plan: item,
            },
          };

          plannerItemsData.push({
            ...generalData,
            x: new Date(item.planned_event.end).getTime(),
          });
          actualItemsData.push({
            ...generalData,
            x: actual?.end ? new Date(actual.end).getTime() : null,
          });

          yLevel++;
        });

        if (i !== Object.keys(plannerItems).length - 1) {
          yPlotLines.push({
            color: "#989898",
            width: 1,
            value: yLevel - 0.5,
            zIndex: 5,
          });
        }

        plansSeriesData[sectionName] = {
          plans: plannerItemsData,
          actual: actualItemsData,
          zones: [],
        };
      });

      for (const sectionName in plansSeriesData) {
        const data = plansSeriesData[sectionName].actual;
        for (let i = data.length - 2; i >= 0; i--) {
          if (data[i].x === null) {
            data.pop();
          } else {
            break;
          }
        }

        let shiftTo = 0;
        for (let i = 0; i < data.length; i++) {
          if (data[i].x === null) {
            shiftTo++;
          } else {
            break;
          }
        }

        plansSeriesData[sectionName].actual = data.slice(shiftTo);
      }

      for (const sectionName in plansSeriesData) {
        const data = plansSeriesData[sectionName];

        data.actual.forEach((item, i) => {
          if (item.x === null && i !== 0) {
            item.x = Date.now();

            if (data.zones.at(-1)?.value === i) {
              const zone = data.zones.at(-1) as Record<string, unknown>;
              zone.value = i + 1;
            } else {
              data.zones.push(
                { value: item.y - 1 },
                { value: item.y + 1, color: this.getSeriesColor("actual"), dashStyle: "Dash" },
              );
            }
          }
        });
      }

      const items = Object.entries(plansSeriesData);

      const sequences = items.flatMap(([name, data]) => {
        const planId = uuid();

        return [
          {
            name,
            id: planId,
            type: "spline",
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 4,
            },
            lineWidth: 3,
            dataType: "Plan",
            data: data.plans,
            color: this.getSeriesColor("plan"),
          },
          {
            name,
            id: uuid(),
            type: "spline",
            dataType: "Actual",
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 4,
            },
            lineWidth: 3,
            linkedTo: planId,
            data: data.actual,
            zones: data.zones,
            color: this.getSeriesColor("actual"),
          },
        ];
      });

      return {
        yAxis: {
          tickPixelInterval: 2,
          categories: yAxisCategories,
          plotLines: yPlotLines,
          title: {
            text: undefined,
          },
        },
        tooltip: {
          useHTML: true,
          formatter() {
            return getTooltipContent((this as unknown as { point: SeriesDataItem }).point.events);
          },
        },
        series: sequences,
      };
    },
    getChartData() {
      const mode = this.graphModes.find((mode) => mode.current)?.type;
      let data = {};

      if (mode === "end-date") data = this.getEndDateChartData();
      else if (mode === "sequence") data = this.getSequenceGroupedChartData();

      return {
        chart: {
          height: "60%",
          zoomType: "y",
        },
        global: {
          lang: {
            resetZoom: "he",
          },
        },
        title: {
          text: undefined,
        },
        legend: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        plotOptions: {
          series: {
            states: {
              inactive: {
                opacity: 1,
              },
            },
          },
        },
        xAxis: {
          type: "datetime",
          dateTimeLabelFormats: {
            week: "%e. %B",
            month: "%B '%y",
          },
          plotLines: [
            {
              color: "#fa5252",
              dashStyle: "Dot",
              width: 1,
              value: Date.now(),
              zIndex: 5,
              label: {
                rotation: 0,
                textAlign: "left",
                x: -1,
                useHTML: true,
                formatter(): string {
                  return `<div style="background:#fa5252;padding: 4px 8px; color: white; border-radius: 0 5px 5px 0">${format(
                    new Date(),
                    "dd.MM.yyyy",
                  )}</div>`;
                },
              },
            },
          ],
        },
        ...data,
      };
    },
  },
});
